/**
 * Pet Mindful · Carrito lateral
 *
 * Intercepta el botón del carrito y los formularios de «Agregar» para que
 * todo ocurra sin recargar. Si algo falla —red caída, JS bloqueado— el
 * formulario se envía como siempre y la persona termina en /cart, que
 * funciona igual: el drawer es una mejora, no un requisito.
 *
 * El repintado va por la Section Rendering API en vez de armar el HTML aquí:
 * así los precios, los descuentos y el umbral de envío los sigue
 * calculando Liquid, sin duplicar el formato de moneda en javascript.
 */
const PM_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class PMCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.pmSection;
    this.panel = this.querySelector('.pm-cart__panel');
    this.scrim = this.querySelector('.pm-cart__scrim');
    this.opener = null;

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => this.onKeydown(event));

    // El botón del carrito del header vive fuera de este componente
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-pm-cart-toggle]');
      if (!toggle) return;
      event.preventDefault();
      this.opener = toggle;
      this.open();
    });

    // Cualquier «Agregar» del tema entra por aquí
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;
      event.preventDefault();
      this.addFromForm(form);
    });

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.sectionId) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.sectionId) this.close();
      });
    }
  }

  onClick(event) {
    if (event.target.closest('[data-pm-cart-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const qty = event.target.closest('[data-pm-qty]');
    if (qty) {
      event.preventDefault();
      const line = qty.closest('[data-pm-line]');
      if (line) this.changeLine(Number(line.dataset.pmLine), Number(qty.dataset.pmQty));
      return;
    }

    const quick = event.target.closest('[data-pm-quick-add]');
    if (quick) {
      event.preventDefault();
      this.add({ id: quick.dataset.pmQuickAdd, quantity: 1 }, quick);
    }
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    // Con el drawer abierto el tabulador no debe escaparse al fondo
    const items = Array.from(this.panel.querySelectorAll(PM_FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open() {
    if (this.classList.contains('is-open')) return;

    if (this.scrim) this.scrim.hidden = false;
    if (this.panel) this.panel.hidden = false;

    // Un frame de margen para que la transición arranque desde el estado cerrado
    window.requestAnimationFrame(() => this.classList.add('is-open'));

    document.body.style.overflow = 'hidden';
    if (this.panel) this.panel.focus({ preventScroll: true });
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    document.body.style.overflow = '';

    const hide = () => {
      if (!this.classList.contains('is-open')) {
        if (this.scrim) this.scrim.hidden = true;
        if (this.panel) this.panel.hidden = true;
      }
    };
    // Se oculta al terminar la animación; el timeout cubre reduced-motion
    if (this.panel) {
      this.panel.addEventListener('transitionend', hide, { once: true });
    }
    window.setTimeout(hide, 400);

    if (this.opener && document.contains(this.opener)) {
      this.opener.focus({ preventScroll: true });
    }
    this.opener = null;
  }

  busy(state) {
    this.classList.toggle('is-busy', state);
  }

  addFromForm(form) {
    const button = form.querySelector('[data-pm-add]');
    this.opener = null;

    const body = new FormData(form);
    this.post('/cart/add.js', body, button).then((ok) => {
      // Si el servidor rechaza (sin stock, por ejemplo) se deja el flujo normal
      if (!ok) form.submit();
    });
  }

  add(payload, button) {
    this.post('/cart/add.js', JSON.stringify(payload), button, true);
  }

  post(url, body, button, isJson) {
    const original = button ? button.innerHTML : null;
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    this.busy(true);

    const options = { method: 'POST', body };
    if (isJson) options.headers = { 'Content-Type': 'application/json' };

    return fetch(url, options)
      .then((response) => {
        if (!response.ok) throw new Error(`carrito: ${response.status}`);
        return response.json();
      })
      .then(() => this.refresh())
      .then(() => {
        this.open();
        return true;
      })
      .catch((error) => {
        console.error('pm-cart:', error);
        return false;
      })
      .finally(() => {
        this.busy(false);
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
          if (original !== null) button.innerHTML = original;
        }
      });
  }

  changeLine(line, quantity) {
    this.busy(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`carrito: ${response.status}`);
        return response.json();
      })
      .then(() => this.refresh())
      .catch((error) => {
        console.error('pm-cart:', error);
        // Sin respuesta fiable, /cart es la fuente de verdad
        window.location.href = '/cart';
      })
      .finally(() => this.busy(false));
  }

  /** Repinta el interior del drawer y el contador del header. */
  refresh() {
    if (!this.sectionId) return Promise.resolve();

    return fetch(`${window.location.pathname}?sections=${encodeURIComponent(this.sectionId)}`)
      .then((response) => response.json())
      .then((sections) => {
        const markup = sections[this.sectionId];
        if (!markup) return;

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const fresh = parsed.querySelector('pm-cart-drawer');
        if (!fresh) return;

        const wasOpen = this.classList.contains('is-open');

        ['.pm-cart__body', '.pm-cart__head', '.pm-cart__foot'].forEach((selector) => {
          const next = fresh.querySelector(selector);
          const current = this.querySelector(selector);

          if (next && current) {
            current.replaceWith(next);
          } else if (next && !current) {
            // El pie solo existe con el carrito lleno
            this.panel.appendChild(next);
          } else if (!next && current) {
            current.remove();
          }
        });

        this.panel = this.querySelector('.pm-cart__panel');
        if (wasOpen) this.classList.add('is-open');

        this.syncCount(fresh);
        // La ficha recalcula su mensaje de envío gratis con el carrito nuevo
        document.dispatchEvent(new CustomEvent('pm:cart:updated'));
      });
  }

  syncCount(fresh) {
    const source = fresh.querySelector('.pm-cart__count');
    if (!source) return;

    const total = (source.textContent.match(/\d+/) || ['0'])[0];
    document.querySelectorAll('[data-pm-cart-count]').forEach((node) => {
      node.textContent = total;
      node.classList.toggle('pm-header__count--empty', total === '0');
    });
  }
}

if (!customElements.get('pm-cart-drawer')) {
  customElements.define('pm-cart-drawer', PMCartDrawer);
}
